import type { UserRole } from "@workspace/api-client";
import {
  BarChart3,
  Briefcase,
  CalendarDays,
  CalendarRange,
  Factory,
  MessageSquare,
  Monitor,
  Settings,
  Settings2,
  ShieldAlert,
  Sparkles,
  ShieldCheck,
  History,
  Truck,
  Users,
  type LucideIcon,
} from "lucide-react";

export type AppRole = UserRole | "superadmin";
export type NavSectionId = "operaciones" | "reportes" | "recursos" | "catalogos" | "sistema";

export interface NavSectionMeta {
  id: NavSectionId;
  label: string;
  description: string;
}

export interface NavItem {
  href: string;
  label: string;
  description: string;
  icon: LucideIcon;
  roles: AppRole[];
  section: NavSectionId;
  homeFor?: AppRole[];
  featureFlag?: string;
  /** When set, the sidebar hides this item if the user lacks this permission (in addition to role check). */
  requiredPermission?: string;
}

export const NAV_SECTIONS: NavSectionMeta[] = [
  { id: "operaciones", label: "Operaciones", description: "Planificación, ejecución y monitoreo diario" },
  { id: "reportes", label: "Reportes", description: "Tableros ejecutivos y métricas de la operación" },
  { id: "recursos", label: "Recursos", description: "Activos, personas y documentación operativa" },
  { id: "catalogos", label: "Parámetros", description: "Tarifas y parámetros de negocio" },
  { id: "sistema", label: "Sistema", description: "Administración técnica y permisos" },
];

export const NAV_ITEMS: NavItem[] = [
  {
    href: "/programacion",
    label: "Programación",
    description: "Agenda diaria y asignación operativa",
    icon: CalendarDays,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "operaciones",
    homeFor: ["admin", "logistica"],
    requiredPermission: "programacion:view",
  },
  {
    href: "/monitor",
    label: "Monitor en Vivo",
    description: "Estado actual de servicios y alertas",
    icon: Monitor,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "operaciones",
    requiredPermission: "monitor:view",
  },
  {
    href: "/servicios",
    label: "Servicios",
    description: "Altas, seguimiento y ajustes de servicio",
    icon: Briefcase,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "operaciones",
    requiredPermission: "services:read",
  },
  {
    href: "/disponibilidad",
    label: "Disponibilidad",
    description: "Mapa de calor de choferes y vehículos",
    icon: CalendarRange,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "operaciones",
    requiredPermission: "disponibilidad:view",
  },
  {
    href: "/reportes",
    label: "Reportes",
    description: "Dashboard operativo, tablero ejecutivo y exportación",
    icon: BarChart3,
    roles: ["admin", "logistica", "gerencia", "superadmin", "comercial", "chofer"],
    section: "reportes",
    homeFor: ["gerencia", "superadmin", "comercial"],
    requiredPermission: "reportes:view",
  },
  {
    href: "/clientes",
    label: "Clientes y Locaciones",
    description: "Clientes y sus puntos de retiro",
    icon: Users,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "recursos",
    requiredPermission: "clients:read",
  },
  {
    href: "/negocios",
    label: "Negocios / Subclientes",
    description: "Negocios y subclientes por cliente",
    icon: Briefcase,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "recursos",
    requiredPermission: "clients:read",
  },
  {
    href: "/plantas-descarga",
    label: "Plantas de tratamiento",
    description: "Plantas y puntos de tratamiento compartidos",
    icon: Factory,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "recursos",
    requiredPermission: "plants:read",
  },
  {
    href: "/vehiculos",
    label: "Flota",
    description: "Vehículos, acoplados y equipamiento",
    icon: Truck,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "recursos",
    requiredPermission: "vehicles:read",
  },
  {
    href: "/personal",
    label: "Personal",
    description: "Legajos, roles y vacaciones",
    icon: Users,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "recursos",
    requiredPermission: "personnel:read",
  },
  {
    href: "/certificaciones",
    label: "Certificaciones",
    description: "Vencimientos y asignaciones por recurso",
    icon: ShieldCheck,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "recursos",
    requiredPermission: "certifications:read",
  },
  {
    href: "/tipos-servicio",
    label: "Tipos de Servicio",
    description: "Parámetros del servicio y comportamiento",
    icon: Settings,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "catalogos",
    requiredPermission: "catalogs:read",
  },
  {
    href: "/tipos-residuo",
    label: "Tipos de Residuo",
    description: "Clasificación y categorías operativas",
    icon: Settings,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "catalogos",
    requiredPermission: "catalogs:read",
  },
  {
    href: "/tipos-flota",
    label: "Tipos de Flota",
    description: "Tipos de vehículos y acoplados",
    icon: Truck,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "catalogos",
    requiredPermission: "catalogs:read",
  },
  {
    href: "/opcionales",
    label: "Opcionales",
    description: "Adicionales seleccionables por servicio",
    icon: Settings,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "catalogos",
    requiredPermission: "optionals:read",
  },
  {
    href: "/novedades",
    label: "Novedades",
    description: "Qué hay de nuevo en cada versión del sistema",
    icon: Sparkles,
    // Sin chofer a propósito: las novedades son del panel web. Sin permiso
    // requerido: cualquier usuario del panel puede leerlas.
    roles: ["admin", "logistica", "gerencia", "comercial", "superadmin"],
    section: "sistema",
  },
  {
    href: "/auditoria",
    label: "Auditoría",
    description: "Historial consolidado de cambios y bajas",
    icon: History,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer"],
    section: "sistema",
    requiredPermission: "audit:read",
  },
  {
    href: "/usuarios",
    label: "Usuarios",
    description: "Cuentas, permisos y vínculo con personal",
    icon: ShieldAlert,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer", "superadmin"],
    section: "sistema",
    requiredPermission: "users:read",
  },
  {
    href: "/roles-permisos",
    label: "Roles y Permisos",
    description: "Matriz de accesos por rol y por tab",
    icon: ShieldCheck,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer", "superadmin"],
    section: "sistema",
    requiredPermission: "roles_perms:view",
  },
  {
    href: "/configuracion",
    label: "Configuración",
    description: "Feature flags y ajustes globales",
    icon: Settings2,
    roles: ["admin", "logistica", "gerencia", "comercial", "chofer", "superadmin"],
    section: "sistema",
    // The screen only toggles flags, and toggling is feature_flags:manage.
    requiredPermission: "feature_flags:manage",
  },
  {
    href: "/feedback",
    label: "Feedback",
    description: "Bugs, mejoras y observaciones internas",
    icon: MessageSquare,
    roles: ["admin", "superadmin", "logistica", "gerencia", "comercial", "chofer"],
    section: "sistema",
    requiredPermission: "feedback:read",
  },
];

export function getDefaultRoute(role?: string): string {
  const normalizedRole = (role ?? "") as AppRole;
  const preferred = NAV_ITEMS.find((item) => item.homeFor?.includes(normalizedRole));
  if (preferred) return preferred.href;

  const firstVisible = NAV_ITEMS.find((item) => item.roles.includes(normalizedRole));
  return firstVisible?.href ?? "/login";
}
